import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, Instagram, Linkedin, Twitter } from "lucide-react";

// ═══════════════════════════════════════════════════
// INTERACTIVE CURSOR-REVEAL GIANT BRANDING
// Text is invisible until you drag your cursor/finger across it
// Brief flash on scroll-in, then only cursor reveals it
// ═══════════════════════════════════════════════════
function RevealBranding() {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const subRef = useRef(null);
  const [scrollRevealed, setScrollRevealed] = useState(false);
  const [scrollFading, setScrollFading] = useState(false);
  const seenRef = useRef(false);

  // Update mask position via CSS variables (no re-renders)
  const updateMask = useCallback((clientX, clientY) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    el.style.setProperty("--mx", `${x}px`);
    el.style.setProperty("--my", `${y}px`);
    el.style.setProperty("--reveal", "1");
  }, []);

  const handleMouseMove = useCallback((e) => updateMask(e.clientX, e.clientY), [updateMask]);
  const handleTouchMove = useCallback((e) => {
    const t = e.touches[0];
    if (t) updateMask(t.clientX, t.clientY);
  }, [updateMask]);

  const handleMouseLeave = useCallback(() => {
    const el = containerRef.current;
    if (el) el.style.setProperty("--reveal", "0");
  }, []);

  // Scroll-in: flash reveal then fade
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !seenRef.current) {
          seenRef.current = true;
          setScrollRevealed(true);
          setTimeout(() => {
            setScrollFading(true);
            setTimeout(() => {
              setScrollRevealed(false);
              setScrollFading(false);
            }, 1200);
          }, 1400);
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative py-16 md:py-24 overflow-hidden select-none cursor-crosshair"
      style={{ "--mx": "-500px", "--my": "-500px", "--reveal": "0" }}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchMove}
      onMouseLeave={handleMouseLeave}
      data-testid="footer-giant-branding"
    >
      <style>{`
        .reveal-text-container {
          position: relative;
          text-align: center;
          overflow: hidden;
        }
        .reveal-text-main {
          font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
          font-size: 20vw;
          font-weight: 900;
          letter-spacing: -0.04em;
          text-transform: uppercase;
          line-height: 0.85;
          user-select: none;
          color: transparent;
          position: relative;
        }
        .reveal-text-main .strat {
          background: linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0.7));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .reveal-text-main .iq {
          background: linear-gradient(135deg, #3b82f6, #06b6d4);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .reveal-text-sub {
          font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
          font-size: 3.5vw;
          font-weight: 700;
          letter-spacing: 0.35em;
          text-transform: uppercase;
          user-select: none;
          color: rgba(6, 182, 212, 0.7);
          text-shadow: 0 0 40px rgba(6, 182, 212, 0.15);
        }
        .reveal-mask-layer {
          -webkit-mask-image: radial-gradient(
            ellipse 220px 280px at var(--mx) var(--my),
            black 0%, black 30%, transparent 100%
          );
          mask-image: radial-gradient(
            ellipse 220px 280px at var(--mx) var(--my),
            black 0%, black 30%, transparent 100%
          );
          opacity: var(--reveal);
          transition: opacity 0.3s ease;
        }
        .reveal-scroll-flash {
          animation: scrollFlashIn 0.8s ease-out forwards;
        }
        .reveal-scroll-fadeout {
          animation: scrollFlashOut 1.2s ease-in forwards;
        }
        @keyframes scrollFlashIn {
          0% { opacity: 0; transform: scale(0.97); filter: blur(8px); }
          100% { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        @keyframes scrollFlashOut {
          0% { opacity: 1; filter: blur(0); }
          100% { opacity: 0; filter: blur(12px); }
        }
      `}</style>

      {/* Scroll-in flash layer. shows briefly then fades */}
      {scrollRevealed && (
        <div
          className={`reveal-text-container ${scrollFading ? "reveal-scroll-fadeout" : "reveal-scroll-flash"}`}
          style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 5, pointerEvents: "none" }}
        >
          <div className="reveal-text-main" aria-hidden="true">
            <span className="strat">STRAT</span><span className="iq">IQ</span>
          </div>
          <div className="reveal-text-sub mt-2" aria-hidden="true">SOLUTIONS</div>
        </div>
      )}

      {/* Cursor-reveal layer. follows mouse/touch */}
      <div
        ref={textRef}
        className="reveal-text-container reveal-mask-layer"
        style={{ position: "relative", zIndex: 10 }}
      >
        <div className="reveal-text-main" aria-hidden="true">
          <span className="strat">STRAT</span><span className="iq">IQ</span>
        </div>
        <div ref={subRef} className="reveal-text-sub mt-2" aria-hidden="true">SOLUTIONS</div>
      </div>

      {/* Faint hint glow so users know something is there */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
        style={{ zIndex: 1 }}
      >
        <div style={{
          fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
          fontSize: "20vw",
          fontWeight: 900,
          letterSpacing: "-0.04em",
          textTransform: "uppercase",
          lineHeight: 0.85,
          color: "rgba(255,255,255,0.015)",
          userSelect: "none",
        }}>STRATIQ</div>
        <div style={{
          fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
          fontSize: "3.5vw",
          fontWeight: 700,
          letterSpacing: "0.35em",
          textTransform: "uppercase",
          color: "rgba(6,182,212,0.015)",
          userSelect: "none",
          marginTop: "0.5rem",
        }}>SOLUTIONS</div>
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    <footer data-testid="footer" className="relative bg-[#030712] text-white overflow-hidden" role="contentinfo">

      {/* ═══ INTERACTIVE REVEAL GIANT BRANDING ═══ */}
      <RevealBranding />

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent mx-6 md:mx-12" />

      {/* ═══ MAIN FOOTER CONTENT ═══ */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-14 pb-8">

        {/* 4-Column Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8 lg:gap-14">

          {/* Column 1: Products */}
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/50 mb-5">Products</p>
            <div className="space-y-3">
              {[
                { label: "SmartSite™ Lite", to: "/product/smartsite-lite" },
                { label: "SmartSite™ Standard", to: "/product/smartsite-standard" },
                { label: "SmartSite™ Premium", to: "/product/smartsite-premium" },
                { label: "SmartSite™ Pro", to: "/product/smartsite-pro" },
                { label: "All Products", to: "/products" },
                { label: "Pricing", to: "/pricing" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="block text-[13px] text-white/60 font-medium hover:text-white transition-colors duration-200" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Column 2: Company */}
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/50 mb-5">Company</p>
            <div className="space-y-3">
              {[
                { label: "About Us", to: "/about" },
                { label: "SmartMatch", to: "/smartmatch" },
                { label: "Book a Call", to: "/book" },
                { label: "Contact", to: "/contact" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="block text-[13px] text-white/60 font-medium hover:text-white transition-colors duration-200" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Column 3: Legal */}
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/50 mb-5">Legal</p>
            <div className="space-y-3">
              {[
                { label: "Privacy Policy", to: "/legal/privacy" },
                { label: "Terms of Service", to: "/legal/terms" },
                { label: "Cookie Policy", to: "/legal/cookies" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="block text-[13px] text-white/60 font-medium hover:text-white transition-colors duration-200" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Column 4: Contact + CTA */}
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/50 mb-5">Get in Touch</p>
            <div className="space-y-3 mb-6">
              <a href="mailto:info@stratiqsolutions.co.uk" className="flex items-center gap-2 text-[13px] text-white/60 font-medium hover:text-white transition-colors break-all" data-testid="footer-email">
                <Mail size={13} className="text-white/50 flex-shrink-0" />
                <span className="break-all">info@stratiqsolutions.co.uk</span>
              </a>
              <a href="tel:08002740058" className="flex items-center gap-2 text-[13px] text-white/60 font-medium hover:text-white transition-colors" data-testid="footer-phone">
                <Phone size={13} className="text-white/50 flex-shrink-0" />
                0800 274 0058
              </a>
            </div>
            <Link
              to="/book"
              data-testid="footer-cta-book"
              className="inline-flex items-center gap-2 bg-cyan-500 text-slate-950 rounded-full px-5 py-2.5 text-[12px] font-bold transition-all hover:bg-cyan-400 hover:-translate-y-0.5 shadow-lg shadow-cyan-500/15"
            >
              Book a Call <ArrowRight size={12} />
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-white/[0.04]">
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-[11px] text-white/50 font-medium">&copy; {new Date().getFullYear()} STRATIQ Solutions Ltd. All rights reserved.</p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {/* Social Icons */}
              <div className="flex items-center gap-3">
                {[
                  { icon: Instagram, href: "#", label: "Instagram" },
                  { icon: Linkedin, href: "#", label: "LinkedIn" },
                  { icon: Twitter, href: "#", label: "Twitter" },
                ].map((social) => (
                  <a key={social.label} href={social.href} aria-label={social.label} data-testid={`footer-social-${social.label.toLowerCase()}`}
                    className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.05] flex items-center justify-center text-white/40 hover:text-white hover:border-white/[0.15] transition-all duration-200">
                    <social.icon size={13} />
                  </a>
                ))}
              </div>
              {/* Legal inline links */}
              <div className="flex items-center gap-4 text-[11px] text-white/50 font-medium flex-wrap justify-center">
                <Link to="/legal/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
                <Link to="/legal/terms" className="hover:text-white/60 transition-colors">Terms</Link>
                <Link to="/legal/cookies" className="hover:text-white/60 transition-colors">Cookies</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
